import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import { useGetMisbilledHCPCS, useGetMisbilledHCPCSProducts } from '@/hooks/service-hooks/billingTools';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { getMisbilledHCPCS } from '@/services/billingTools';
import type { BillingToolsProducts, MisbilledHCPCS } from '@/types/billingTools';
import type { FilterType } from '@/types/table';
import { formatDateToString } from '@/utils/dateUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { missBilledUnitsHCPCSColumns } from '../billingTools';

const CLINIC_KEY = 'clinic';

/**
 * MisbilledHCPCSTable Component
 *
 * Displays transactions where the billed HCPCS code does not match the product's correct Q-code.
 * Helps identify billing discrepancies that need correction.
 *
 * Features:
 * - Real-time display of misbilled HCPCS transactions
 * - Shows product, patient info, clinic, physician, and Q-code discrepancies
 * - Filterable by clinic
 * - Export functionality (CSV, Excel)
 * - Integrated with app tour system
 */
const MisbilledHCPCSTable = () => {
	// Shared clinic filter helpers (key used by Misbilled HCPCS API)
	const { defaultClinicNames, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	const [products, setProducts] = useState<BillingToolsProducts[]>([]);

	// Table filter state: manages pagination, search, sorting, and tab selection
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		globalFilters: JSON.stringify(
			defaultClinicNames.length
				? [
					{
						key: CLINIC_KEY,
						values: defaultClinicNames,
					},
				]
				: [],
		),
		search: '',
		enabled: defaultClinicNames.length > 0,
		tab: '',
		sort: '',
	});

	const columns = useMemo(() => missBilledUnitsHCPCSColumns, []);

	// Fetch misbilled HCPCS data based on current filters
	const { data, isFetching } = useGetMisbilledHCPCS({
		...filters,
		enabled: JSON.parse(filters.globalFilters || '[]').length > 0,
	});

	// State and effect to fetch products for product filter options
	const { data: productsData } = useGetMisbilledHCPCSProducts({
		globalFilters: JSON.stringify([
			{
				key: CLINIC_KEY,
				values: defaultClinicNames,
			},
		]),
		enabled: defaultClinicNames.length > 0 && products.length === 0,
	});

	useEffect(() => {
		if (productsData?.items?.length && !products.length) {
			setProducts(productsData.items);
		}
	}, [products.length, productsData]);

	/**
	 * Transform misbilled HCPCS data for table display
	 * Converts raw API data into table-ready format with:
	 * - Formatted procedure dates
	 * - Product and patient information
	 * - Clinic and physician details
	 * - Q-code discrepancies (BillingQCode vs correct qcode)
	 */
	const transformedData = useMemo(() => {
		if (!data?.items) return [];
		const misbilledHCPCSData = data.items;

		return misbilledHCPCSData.map((item) => {
			return {
				id: item.tatransactionid,
				Product: item.Product ? (
					<FaceliftTruncatedTextWithTooltip text={item.Product} maxLength={25} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={25} />
				),
				emrid: item.emrid ? (
					<FaceliftTruncatedTextWithTooltip text={item.emrid} maxLength={15} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={15} />
				),
				PatientLastName: item.PatientLastName ? (
					<FaceliftTruncatedTextWithTooltip text={item.PatientLastName} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				clinic: item.clinic ? (
					<FaceliftTruncatedTextWithTooltip text={item.clinic} maxLength={30} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={30} />
				),
				PhysicianName: item.PhysicianName ? (
					<FaceliftTruncatedTextWithTooltip text={item.PhysicianName} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				BillingQCode: item.BillingQCode || '--',
				qcode: item.qcode || '--',
				proceduredos: item.proceduredos ? formatDateToString(new Date(item.proceduredos)) : '--',
				tatransactionid: item.tatransactionid ? (
					<FaceliftTruncatedTextWithTooltip text={String(item.tatransactionid)} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
			};
		});
	}, [data]);

	/**
	 * Transform misbilled HCPCS data for export
	 * Converts data to plain text format for CSV/Excel compatibility
	 */
	const transformedDataToExport = (misbilledHCPCSData: MisbilledHCPCS[]) => {
		if (!misbilledHCPCSData.length) return [];

		return misbilledHCPCSData.map((item) => {
			return {
				id: item.tatransactionid,
				Product: item.Product || '--',
				emrid: item.emrid || '--',
				PatientLastName: item.PatientLastName || '--',
				clinic: item.clinic || '--',
				PhysicianName: item.PhysicianName || '--',
				BillingQCode: item.BillingQCode || '--',
				qcode: item.qcode || '--',
				proceduredos: item.proceduredos ? formatDateToString(new Date(item.proceduredos)) : '--',
				tatransactionid: item.tatransactionid || '--',
			};
		});
	};

	/**
	 * Handle filter changes from DataTable
	 * Updates filter state to trigger new data fetch with applied filters
	 */
	const onFilterApply = useCallback(
		({
			page,
			limit,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters, with clinic names in this case
			const mappedFilters = mapGlobalFilters(globalFilters || [], true);

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				globalFilters: JSON.stringify(mappedFilters),
				search,
				enabled: defaultClinicNames.length > 0,
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[mapGlobalFilters],
	);

	/**
	 * Fetch all misbilled HCPCS records for export
	 * Iterates through all pages to get complete dataset with current filters applied
	 * @returns Complete misbilled HCPCS data formatted for export
	 */
	const fetchAllDataToExport = async () => {
		let allItems: MisbilledHCPCS[] = [];
		let page = 1;
		let hasMore = true;

		while (hasMore) {
			const response = await getMisbilledHCPCS(
				page,
				9999,
				filters.search,
				(filters.status as string) || '',
			);

			if (response?.items?.length) {
				allItems = allItems.concat(response.items);
				const totalItems = response?.meta?.totalItems || 0;
				hasMore = allItems.length < totalItems;
				page += 1;
			} else {
				hasMore = false;
			}
		}

		const misbilledHCPCSDataForExcel = { items: allItems };
		const transformedData = transformedDataToExport(
			misbilledHCPCSDataForExcel.items || [],
		);

		return transformedData;
	};

	/**
	 * Export misbilled HCPCS data to CSV file
	 */
	const exportAllAsCSV = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateCSVFromTable(
				columns,
				transformedInvoiceData,
				`MisbilledHCPCS-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	/**
	 * Export misbilled HCPCS data to Excel file
	 */
	const exportAllAsExcel = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateExcelFromTable(
				columns,
				transformedInvoiceData,
				`MisbilledHCPCS-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			borderRadius="md"
			boxShadow="sm"
		>
			<DataTable
				tableName="Misbilled HCPCS"
				loading={isFetching}
				items={transformedData}
				columns={columns}
				topLabel="Misbilled HCPCS"
				totalCount={data?.meta.totalItems || 0}
				onFilterApply={onFilterApply}
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				globalFilter={{
					title: 'Filters',
					items: [
						{
							options: defaultClinicNames.map((clinic) => ({
								label: clinic,
								id: clinic,
							})),
							key: 'Clinics',
							name: 'Clinics',
						},
						{
							options: products.map((product) => ({
								label: product.Product,
								id: product.Product,
							})),
							key: 'Product',
							name: 'Products',
						},
					],
				}}
				actions={[]}
				exportable
				hideAllStatusFilter={true}
				csv
				excel
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>
		</Box>
	);
};

export default MisbilledHCPCSTable;
